import { useRef } from 'react';

import { Field, FieldDescription, FieldLabel } from '@/components/ui/field.tsx';
import { RadioGroupItem } from '@/components/ui/radio-group.tsx';
import { cn } from '@/lib/utils.ts';
import type { Product } from '@/types/product.type.ts';

type Props = {
  product: Product;
  checked?: boolean;
  noDescription?: boolean;
  onSelect?: () => void;
  onDeselect?: () => void;
};

export default function ProductRadioItem({
  product,
  checked,
  noDescription,
  onSelect,
  onDeselect,
}: Props) {
  const radioRef = useRef<HTMLButtonElement>(null);
  const showDescription = !noDescription;

  const handleClick = (e: React.MouseEvent) => {
    // Prevent default label behavior since we're handling selection manually
    // This fixes dead clicks caused by display:contents on the FieldLabel
    e.preventDefault();
    // Stop propagation to prevent Radix RadioGroup item handler from
    // re-selecting the item after we've handled the click (especially for deselect)
    e.stopPropagation();

    if (checked && onDeselect) {
      onDeselect();
    } else if (!checked && onSelect) {
      onSelect();
    }

    // Focus the radio to preserve keyboard navigation
    radioRef.current?.focus();
  };

  return (
    <Field
      orientation="none"
      onClickCapture={handleClick}
      className={cn(
        'relative flex cursor-pointer flex-col gap-0 rounded-xl shadow',
        checked && 'outline-2 outline-offset-[-2px] outline-hydrangea-500'
      )}
      style={{
        boxShadow:
          '0 0 1.112px 0 rgba(0, 0, 0, 0.05), 0 0 2.225px 0 rgba(0, 0, 0, 0.08), 0 0 11.124px 0 rgba(0, 0, 0, 0.10)',
      }}
    >
      <FieldLabel htmlFor={`product-radio-${product.uuid}`} className="contents cursor-pointer">
        <img
          src={product.image}
          alt={product.name}
          className="h-[150px] w-full bg-[#f5f0ea] object-contain"
        />

        <div className="flex flex-col p-2">
          <h3 className="flex items-center font-circular text-base font-medium">{product.name}</h3>

          {showDescription && product.description && (
            <FieldDescription className="font-circular text-sm font-normal text-gray-400">
              {product.description}
            </FieldDescription>
          )}
        </div>
      </FieldLabel>

      <RadioGroupItem
        ref={radioRef}
        id={`product-radio-${product.uuid}`}
        className="absolute top-2.5 left-2.5"
        value={product.uuid}
      />
    </Field>
  );
}
